import { useState } from 'react'
import { useApp } from '../../context/AppContext'
import { sileo } from 'sileo'

/**
 * Componente de formulario para la creación de nuevas prioridades en la base de datos.
 * * * Gestiona la inserción de registros en la tabla `priority` del esquema `Tickets`
 * mediante Supabase, validando los datos de entrada y notificando al usuario
 * sobre el resultado de la operación.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Function} [props.onAdded] - Callback opcional que se ejecuta tras una inserción exitosa.
 * Recibe el objeto del registro recién creado.
 * * @returns {JSX.Element} Un formulario en línea con campos para nombre, nivel y botón de envío.
 */
const AddPriority = ({ onAdded }) => {
  /** @type {Object} Cliente de Supabase extraído del contexto global de la aplicación */
  const { supabase } = useApp()

  /** * @type {[Object, Function]} Estado para los datos del formulario
   * @property {string} name - Nombre descriptivo de la prioridad.
   * @property {number} level - Valor numérico que representa la jerarquía de la prioridad.
   */
  const [newPayload, setNewPayload] = useState({
    name: '',
    level: 0,
  })

  /** @type {[boolean, Function]} Estado para controlar el indicador de carga durante el guardado */
  const [loading, setLoading] = useState(false)

  /**
   * Maneja los cambios en los inputs del formulario.
   * * Asegura que el campo 'level' sea tratado siempre como un valor numérico.
   * @param {React.ChangeEvent<HTMLInputElement>} e - Evento de cambio nativo de React.
   */
  const handleChange = (e) => {
    const { name, value } = e.target

    setNewPayload((prev) => ({
      ...prev,
      [name]: name === 'level' ? Number(value) : value,
    }))
  }

  /**
   * Procesa el envío del formulario.
   * * 1. Valida que el nombre no esté vacío.
   * 2. Realiza la inserción asíncrona en el esquema 'Tickets'.
   * 3. Muestra notificaciones de éxito o error vía `sileo`.
   * 4. Limpia el formulario y notifica al componente padre si la operación es exitosa.
   * * @param {React.FormEvent} e - Evento de envío del formulario.
   */
  async function handleSubmit(e) {
    e.preventDefault()

    if (!newPayload.name.trim()) {
      return sileo.warning('El nombre es requerido')
    }

    try {
      setLoading(true)

      const { data, error } = await supabase
        .schema('Tickets') // 👈 ajusta si tu schema es otro
        .from('priority') // 👈 ajusta al nombre real de tu tabla
        .insert([newPayload])
        .select()
        .single()

      if (error) throw error

      sileo.success('Prioridad agregada correctamente')

      // limpiar form
      setNewPayload({
        name: '',
        level: 0,
      })

      // callback al padre
      if (onAdded) onAdded(data)
    } catch (error) {
      console.error(error)

      sileo.error({
        title: 'Error de la app',
        description: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="pf">
      <div className="pf-row">
        <div className="pf-field pf-grow">
          <label className="pf-label" htmlFor="add-priority-name">
            Nombre
          </label>
          <input
            id="add-priority-name"
            type="text"
            name="name"
            placeholder="Ej. Alta"
            value={newPayload.name}
            onChange={handleChange}
            className="form-control form-control-sm"
            autoFocus
          />
        </div>

        <div className="pf-field" style={{ width: 110 }}>
          <label className="pf-label" htmlFor="add-priority-level">
            Nivel
          </label>
          <input
            id="add-priority-level"
            type="number"
            name="level"
            placeholder="Nivel"
            value={newPayload.level}
            onChange={handleChange}
            className="form-control form-control-sm"
          />
        </div>
      </div>

      <div className="pf-hint">A mayor número, mayor prioridad.</div>

      <div className="pf-actions">
        <button
          type="submit"
          className="btn btn-sm btn-primary"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Guardando...
            </>
          ) : (
            <>
              <i className="fa-solid fa-plus me-2" />
              Agregar
            </>
          )}
        </button>
      </div>

      <style>{`
        .pf { display: flex; flex-direction: column; gap: 10px; }
        .pf-row { display: flex; gap: 10px; }
        .pf-field { display: flex; flex-direction: column; gap: 4px; }
        .pf-grow { flex: 1; min-width: 0; }
        .pf-label {
          margin: 0; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.04em;
          text-transform: uppercase; color: var(--gm-text-muted);
        }
        .pf-hint { font-size: 0.75rem; color: var(--gm-text-muted); }
        .pf-actions { display: flex; justify-content: flex-end; gap: 6px; }
      `}</style>
    </form>
  )
}

export default AddPriority
